import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, IndianRupee, AlertTriangle, PackageCheck, Store, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { Chip } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import { formatINR } from "@/lib/ui";
import api from "@/lib/api";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</span>
        <Icon size={17} className={accent} />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}

const FLOW = ["Sales Channels", "Dasarro OMS", "Brand", "Order Routing", "Fulfillment", "Auto Tasks", "QC / Processing", "Packing", "Delivery", "Finance", "Settlement"];

export default function ControlTower() {
  const { selectedBrand } = useBrand();
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/control-tower", { params: { brand: selectedBrand } }).then((r) => setD(r.data));
  }, [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const st = d?.by_status || {};
  const stages = ["New", "Admin Approval", "Payment Pending", "Approved", "Processing", "QC Pending", "Packing", "Ready for Dispatch", "Out for Delivery", "Delivered", "Completed"];

  return (
    <div>
      <PageHeader title="Dasarro Control Tower" subtitle={`Live operations · ${selectedBrand}`} />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Kpi icon={Store} label="Brands" value={d?.total_brands ?? "—"} accent="text-[#4DA3FF]" />
          <Kpi icon={ShoppingCart} label="Orders" value={d?.total_orders ?? "—"} accent="text-white" />
          <Kpi icon={IndianRupee} label="Revenue" value={d ? formatINR(d.revenue) : "—"} accent="text-[#00FF66]" />
          <Kpi icon={PackageCheck} label="Fulfillment Pending" value={d?.fulfillment_pending ?? "—"} accent="text-[#FFD600]" />
          <Kpi icon={AlertTriangle} label="Action Center" value={d?.open_exceptions ?? "—"} accent="text-[#FF6259]" />
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-medium">Orders by Status</h3>
            <button data-testid="ct-view-orders" onClick={() => navigate("/orders")} className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white flex items-center gap-1">Orders <ArrowUpRight size={13} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {stages.filter((s) => st[s]).map((s) => (
              <div key={s} className="border border-white/10 bg-[#0a0a0a] rounded-sm p-3">
                <div className="font-display text-2xl font-bold">{st[s]}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">{s}</div>
              </div>
            ))}
            {Object.keys(st).length === 0 && <p className="text-sm text-zinc-600">No orders yet.</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
            <h3 className="font-display text-lg font-medium mb-4">Orders by Channel</h3>
            {Object.entries(d?.by_channel || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                <span className="text-zinc-300">{k}</span><span className="font-mono text-zinc-400">{v}</span>
              </div>
            ))}
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
            <h3 className="font-display text-lg font-medium mb-4">Orders by Brand</h3>
            {Object.entries(d?.by_brand || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-white/5 text-sm">
                <span className="text-zinc-300">{k}</span><span className="font-mono text-zinc-400">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
          <h3 className="font-display text-lg font-medium mb-4">Operating Flow</h3>
          <div className="flex flex-wrap items-center gap-2">
            {FLOW.map((s, i) => (
              <React.Fragment key={s}>
                <Chip className="bg-white/[0.03] border-white/10 text-zinc-400">{s}</Chip>
                {i < FLOW.length - 1 && <span className="text-zinc-700">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
